import { useCallback, useEffect, useRef } from 'react';
import type { Comment } from '../types.ts';

interface Props {
  frame: number;
  frameCount: number;
  /** Source clip's first frame number, added to stored frames only for the marker tooltip. */
  startFrame?: number;
  buffered: number;
  comments: Comment[];
  onSeek: (frame: number) => void;
  disabled?: boolean;
}

/**
 * Frame scrubber: a thin seek bar under the clip. Frame-anchored comments show up as markers
 * you can click straight to.
 */
export function Scrubber({
  frame,
  frameCount,
  startFrame = 0,
  buffered,
  comments,
  onSeek,
  disabled,
}: Props): React.JSX.Element {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);

  const seekFromClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track || frameCount <= 0) return;
      const rect = track.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      onSeek(Math.round(ratio * (frameCount - 1)));
    },
    [frameCount, onSeek],
  );

  useEffect(() => {
    const onMove = (e: PointerEvent): void => {
      if (dragging.current) seekFromClientX(e.clientX);
    };
    const onUp = (): void => {
      dragging.current = false;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [seekFromClientX]);

  const position = frameCount > 1 ? frame / (frameCount - 1) : 0;

  return (
    <div
      className={`scrubber${disabled ? ' scrubber--disabled' : ''}`}
      ref={trackRef}
      onPointerDown={(e) => {
        if (disabled) return;
        dragging.current = true;
        seekFromClientX(e.clientX);
      }}
    >
      <div className="scrubber__buffered" style={{ width: `${buffered * 100}%` }} />
      <div className="scrubber__played" style={{ width: `${position * 100}%` }} />

      {comments
        .filter((c) => c.frame !== null && frameCount > 1)
        .map((c) => (
          <button
            key={c.id}
            type="button"
            className={`scrubber__marker${c.annotations.length > 0 ? ' scrubber__marker--drawn' : ''}`}
            style={{ left: `${((c.frame ?? 0) / (frameCount - 1)) * 100}%` }}
            title={`${c.author} @ f${startFrame + (c.frame ?? 0)}: ${c.body.slice(0, 80)}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onSeek(c.frame ?? 0)}
          />
        ))}

      <div className="scrubber__playhead" style={{ left: `${position * 100}%` }} />
    </div>
  );
}
